import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { ChevronDown } from 'lucide-react';
import { MouseEvent, useState } from 'react';
import LangEnum from '../../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../../translation/TranslationContext';

interface CatalogViewApplyButtonProps {
  hasScopeChoice: boolean;
  isTargetMissing: boolean;
  canApplyForMe: boolean;
  canApplyForAll: boolean;
  blockedForMeReason?: LangEnum;
  onApplyForMe: () => void;
  onApplyForAll: () => void;
}

const CatalogViewApplyButton = ({
  hasScopeChoice,
  isTargetMissing,
  canApplyForMe,
  canApplyForAll,
  blockedForMeReason,
  onApplyForMe,
  onApplyForAll,
}: CatalogViewApplyButtonProps) => {
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const tooltipContent = blockedForMeReason ? translate(blockedForMeReason) : null;

  if (!hasScopeChoice) {
    return (
      <Tooltip TooltipContent={tooltipContent}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          testId={CatalogProductsTestIds.VIEW_APPLY_BUTTON}
          disabled={!canApplyForMe}
          onClick={onApplyForMe}
        >
          {translate(LangEnum.GLOBAL_APPLY)}
        </Button>
      </Tooltip>
    );
  }

  const handleSelect = (action: () => void) => {
    action();
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip TooltipContent={tooltipContent}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          testId={CatalogProductsTestIds.VIEW_APPLY_MENU_BUTTON}
          disabled={isTargetMissing}
          endIcon={<Icon icon={ChevronDown} size="sm" />}
          onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        >
          {translate(LangEnum.GLOBAL_APPLY)}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          testId={CatalogProductsTestIds.VIEW_APPLY_FOR_ME_ITEM}
          disabled={!canApplyForMe}
          aria-disabled={!canApplyForMe || undefined}
          onClick={() => handleSelect(onApplyForMe)}
        >
          <Typography variant="body2" sx={{ px: 1.5, py: 0.5 }}>
            {translate(LangEnum.CATALOGS_VIEW_APPLY_FOR_ME)}
          </Typography>
        </MenuItem>
        <MenuItem
          testId={CatalogProductsTestIds.VIEW_APPLY_FOR_ALL_ITEM}
          disabled={!canApplyForAll}
          aria-disabled={!canApplyForAll || undefined}
          onClick={() => handleSelect(onApplyForAll)}
        >
          <Typography variant="body2" sx={{ px: 1.5, py: 0.5 }}>
            {translate(LangEnum.CATALOGS_VIEW_APPLY_FOR_ALL)}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default CatalogViewApplyButton;
